<template>
  <b-table
    v-if="orders.length"
    :items="orders"
    responsive
    :fields="orderTableColumns"
    show-empty
    :empty-text="$t('no_matching_records')"
    class="position-relative"
    primary-key="ticketUrl"
  >
    <!-- Column: orderAt -->
    <template #cell(created)="data">
      <span
        class="font-weight-bold"
      >
        {{ momentAgo(data.value.toDate()) }}
      </span>
    </template>

    <!-- Column: From -->
    <template #cell(from)="data">
      <span
        class="font-weight-bold"
      >
        {{ data.value }}
      </span>
    </template>

    <!-- Column: title -->
    <template #cell(title)="data">
      <b-link
        :href="`https://${data.item.ticketData.url}`"
        target="_blank"
        class="font-weight-bold capitalize"
      >
        {{ data.item.ticketData.title }}
      </b-link>
    </template>

    <!-- Column: ticket Url -->
    <template #cell(data)="data">
      <span
        class="font-weight-bold"
      >
        {{ data.item.activeOrders.length }}
      </span>
    </template>

    <!-- Column: Status -->
    <template #cell(status)>
      <b-badge
        pill
        variant="light-success"
        style="text-transform: uppercase"
      >
        {{ $t(`gig_page.order_table.body.open`) }}
      </b-badge>
    </template>

    <!-- Column: Actions -->
    <template
      #cell(actions)="row"
      style="width:235px;"
    >
      <span
        variant="light-success"
        class="capitalize;"
        role="button"
        @click="viewDetails(row)"
      >{{ row.detailsShowing ? $t('gig_page.order_table.body.close_suggestion') : $t('gig_page.order_table.body.see_suggestion') }}
        <feather-icon
          v-if="!row.detailsShowing"
          icon="ChevronDownIcon"
          class="mx-auto"
        />
        <feather-icon
          v-else
          icon="ChevronUpIcon"
          class="mx-auto"
        />
      </span>
    </template>


    <!-- When show details -->
    <template #row-details="row">
      <b-row>
        <b-col
          cols="12"
        >
          <!-- Render rich text content that is not editable -->
          <div style="padding: 10px; border: 1px solid rgba(0,0,0,0.2); border-top-right-radius: 3px; border-top-left-radius: 3px;">
            <p>
              {{ getLatestCommentByCustomer(row.item.ticketData.comments) }}
            </p>
          </div>
          <!-- a table of active orders-->
          <b-table
            dark
            small
            thead-class="d-none"
            :fields="suggestedColumns"
            :items="row.item.activeOrders">
            <template #cell(area)="data">
              <span
                class="font-weight-bold"
              >
                <input-auto-suggest
                  v-model="data.item.selectedAreaName"
                  title="name"
                  placeholder="Valitse alue"
                  :suggestions="data.item.areaSuggestions"
                  :initial-list="data.item.initialAreas"
                  :limit="5"
                  :error-message="data.item.errorMessage"
                  @input="(text, filteredSuggestions) => onInputChange(text, data.item, filteredSuggestions)"
                  @selected="(option) => onSelected(option, data.item)"
                />
              </span>
            </template>
            <template #cell(start)="data">
              <span
                class="font-weight-bold"
              >
                {{ new Date(data.value).toLocaleDateString('fi', {
                  day: 'numeric', month: 'numeric',
                }) }} klo {{ new Date(data.value).toLocaleString('fi', {
                  hour: 'numeric', minute: 'numeric',
                }) }} - {{ new Date(data.item.end).toLocaleString('fi', {
                  hour: 'numeric', minute: 'numeric',
                }) }}
              </span>
            </template>
            <template #cell(role)="data">
              <span
                class="font-weight-bold"
              >
                {{ data.value[0] }}
              </span>
            </template>
            <template #cell(requirements)="data">
              <span
                class="font-weight-bold"
              >
                {{ data.value.join(', ') }}
              </span>
            </template>
            <template #cell(action)="data">
              <b-badge
                pill
                :variant="getVariant(data.value)"
                style="text-transform: uppercase"
              >
                {{ data.value }}
              </b-badge>
            </template>
          </b-table> 
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
        >
          <div style="display: flex; justify-content: right; margin-top: 10px;">
            <b-button
              v-ripple.400="'rgba(234, 84, 85, 0.15)'"
              variant="warning"
              :disabled="row.item.accepting || row.item.rejecting"
              @click="acceptSuggestion(row.item, false)"
            >
              Hylkää ehdotus
              <b-spinner
                v-if="row.item.rejecting"
                small
                type="border"
                variant="light"
              />
            </b-button>
            <b-button
              v-ripple.400="'rgba(234, 84, 85, 0.15)'"
              style="margin-left: 15px;"
              variant="primary"
              :disabled="row.item.accepting || row.item.rejecting || isInvalid(row.item)"
              @click="acceptSuggestion(row.item, true)"
            >
              Luo vuorot
              <b-spinner
                v-if="row.item.accepting"
                small
                type="border"
                variant="light"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-table>
</template>

<script>
import moment from 'moment'
import {
  BButton,
  BRow, BCol,
  BTable, BLink,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import InputAutoSuggest from '@hw-ui/components/input-auto-suggest/InputAutoSuggest.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BLink,
    BBadge,
    BButton,
    BSpinner,
    InputAutoSuggest,
  },
  directives: {
    Ripple,
  },
  props: {

    orderTableColumns: {
      type: Array,
      default: () => [],
    },
    suggestedColumns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedLocation: null,
      selectedShifts: [],
      filteredOptions: [],
      suggestionList: [],
      activedOrder: null,
      orders: [],
      contract: null,
      coordinates: {
        lat: null,
        lon: null,
      },
      processer: {},
      updateStates: [],
    }
  },
  computed: {
    selectedAccount() {
      return this.$store.state.appAccount.selectedAccount
    },
    user() {
      return this.$store.state.appAuth.authUser ? this.$store.state.appAuth.authUser : {}
    },
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser')
      .then(res => {
        const user = JSON.parse(JSON.stringify(res))
        this.processer = {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
        }
      })
  },
  created() {
    this.$emit('fetch-orders', change => {
      const { type, data } = change
      if (type === 'added') {
        this.orders.unshift({ ...data, _showDetails: false, invalid: this.isInvalid(data) })
      }
      if (type === 'removed') {
        this.orders = this.orders.filter(order => order.id !== data.id)
      }
      if (type === 'modified') {
        const index = this.orders.findIndex(order => order.id === data.id)
        if (index !== -1) {
          this.$set(this.orders, index, { ...this.orders[index], ...data, invalid: this.isInvalid(data) })
        } else {
          this.orders.unshift({ ...data, _showDetails: false, invalid: this.isInvalid(data) })
        }
        }
      })
  },
  methods: {
    isInvalid(item) {
      return item.activeOrders.some(o => !o.selectedAreaId)
    },
    momentAgo(date) {
      return moment(date).fromNow()
    },
    getLatestCommentByCustomer(comments) {
      if (comments && comments.length > 0) {
        const customerComments = comments.filter(comment => comment.role === 'End-user')
        const latestComment = customerComments[customerComments.length - 1]
        return latestComment.comment.replace(/-+/g, '-')
      }
      return ''
    },
    getVariant(status) {
      if (status === 'CREATE') {
        return 'light-success'
      }
      if (status === 'UPDATE') {
        return 'light-info'
      }
      if (status === 'DELETE') {
        return 'light-danger'
      }
      return 'light-warning'
    },
    acceptSuggestion(item, accept) {
      if(accept) this.$set(item, 'accepting', true)
      if(!accept) this.$set(item, 'rejecting', true)
      const activeOrderAreaNames = {}
      item.activeOrders.forEach(order => {
        activeOrderAreaNames[order.id] = order.selectedAreaName
      })
      this.$emit('accept-suggestion', item.id, accept, activeOrderAreaNames)
    },
    viewDetails(row) {
      if (!row.detailsShowing) {
        this.orders = this.orders.map(order => {
          if (order.id === row.item.id) {
            this.activedOrder = order
          }
          return {
            ...order,
            _showDetails: order.id === row.item.id,
          }
        })
      } else {
        row.toggleDetails()
      }
    },
    onInputChange(text, item, filteredSuggestions) {
      if(!text) return
      if(text === item.selectedAreaName) {
        this.$set(item, 'selectedAreaId', null)
        this.$set(item, 'selectedAreaName', '')
      }
      if(!filteredSuggestions) return
      this.$set(item, 'areaSuggestions', filteredSuggestions)
    },
    onSelected(option, item) {
      if (option) {
        this.$set(item, 'selectedAreaId', option.id)
        this.$set(item, 'selectedAreaName', option.name)
      }
    },
  },
}
</script>

<style>
    .text-capitalized {
        text-transform: capitalize;
    }
</style>
